import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileSignature, X, Loader, Eye, RotateCcw, Lock } from 'lucide-react';
import { buildLeaseContractPdfBytes, bytesToBase64, base64ToBytes, appendPdf } from '../utils/leaseContract';
import { SignaturePad } from './SignaturePad';
import { buildConditionReportPdfBytes, formToConditionData } from '../utils/conditionReport';

// ── Lease e-signature ─────────────────────────────────────────────────────────
// LeaseSignCard shows in the portal (My Van) when the signed-in driver has a
// pending HPL lease. LeaseSignModal is the review-and-sign surface itself, exported
// so admins can test/complete a signing from the Contracts register too (the
// backend lets admins sign any contract).

const CONTRACT_URL = '/.netlify/functions/vehicle-contract-sb';
const todayLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function contractToData(c, extra = {}) {
  return {
    lessorName: c.lessorName || 'HOLLYPAUL LEASING PTY LTD',
    lessorAbn: c.lessorAbn || '',
    lessee: { entity: c.driverEntity || c.driverName || '', repName: c.driverName || '', abn: c.driverAbn || '' },
    vehicle: c.vehicle || { rego: c.rego },
    startDate: c.startDate || todayLocalISO(),
    term: c.term || undefined,
    paymentDay: c.paymentDay || 'Monday',
    weeklyFee: c.weeklyFee || 0,
    leaseRate: c.leaseRate || 0,
    leaseCap: c.leaseCap || 0,
    excessBasic: c.excessBasic || 2500,
    lessorSignature: c.lessorSignature || null,
    lessorSignatory: c.lessorSignatory || '',
    lessorPosition: c.lessorPosition || '',
    lessorSignedDate: c.lessorSignedDate || '',
    ...extra,
  };
}

// Review-and-sign modal. onSigned(contractId) fires after a successful submit.
export function LeaseSignModal({ contract, onClose, onSigned }) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [signature, setSignature] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [crReport, setCrReport] = useState(null); // structured report (rebuilt w/ signatures)
  const [crBytes, setCrBytes] = useState(null);   // legacy static report PDF (no signatures)
  const driverName = contract.driverName || contract.driverLogin;

  // Fetch the condition report attached to this contract, if any.
  useEffect(() => {
    let alive = true;
    fetch(CONTRACT_URL, { method: 'POST', body: JSON.stringify({ action: 'getContractAttachment', id: contract.id }) })
      .then(r => r.json())
      .then(d => {
        if (!alive || !d.success) return;
        if (d.report) setCrReport(d.report);
        else if (d.pdfBase64) setCrBytes(base64ToBytes(d.pdfBase64));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [contract]);

  // Build lease + condition report as one document. When the driver signs, both the
  // lease AND the report's acknowledgement carry the lessor + lessee signatures.
  const buildCombined = async (lesseeSig) => {
    let bytes = await buildLeaseContractPdfBytes(contractToData(contract, lesseeSig ? { lesseeSignature: lesseeSig, lesseeSignedDate: todayLocalISO() } : {}));
    if (crReport) {
      const crData = formToConditionData(crReport, contract.vehicle || { rego: contract.rego });
      crData.lessorSignature = contract.lessorSignature || null;
      crData.lessorSignatory = contract.lessorSignatory || '';
      crData.lessorPosition = contract.lessorPosition || '';
      crData.lessorSignedDate = contract.lessorSignedDate || '';
      if (lesseeSig) { crData.lesseeSignature = lesseeSig; crData.lesseeSignedDate = todayLocalISO(); }
      bytes = await appendPdf(bytes, await buildConditionReportPdfBytes(crData));
    } else if (crBytes) {
      bytes = await appendPdf(bytes, crBytes); // legacy report (no editable data) — appended unsigned
    }
    return bytes;
  };

  // Preview (unsigned by the driver, but the lessor pre-signature shows).
  useEffect(() => {
    let url = ''; let alive = true;
    (async () => {
      try {
        const bytes = await buildCombined(null);
        if (!alive) return;
        url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
        setPreviewUrl(url);
      } catch { setPreviewUrl(''); }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [contract, crReport, crBytes]);

  const submit = async () => {
    if (!signature) return;
    setBusy(true);
    try {
      const bytes = await buildCombined(signature);
      const res = await fetch(CONTRACT_URL, {
        method: 'POST',
        body: JSON.stringify({ action: 'signContract', id: contract.id, signedPdfBase64: bytesToBase64(bytes) }),
      }).then(r => r.json());
      if (res.success) { if (onSigned) onSigned(contract.id); onClose(); }
      else alert(`Could not submit: ${res.error || 'unknown error'}`);
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1200, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px 12px', overflowY: 'auto' }}>
      <div style={{ width: 680, maxWidth: '100%', background: 'var(--hp-card)', color: 'var(--hp-text)', borderRadius: 14, border: '1px solid var(--hp-border)', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--hp-border)' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Vehicle Lease Agreement</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ padding: 20 }}>
          <p style={{ margin: '0 0 12px', fontSize: '0.84rem', color: 'var(--hp-dim)' }}>
            Please read the agreement for <strong style={{ color: 'var(--hp-text)' }}>{contract.vehicle?.rego || contract.rego}</strong>, then sign below.
          </p>

          {previewUrl ? (
            <iframe title="Lease preview" src={previewUrl} style={{ width: '100%', height: 360, border: '1px solid var(--hp-border)', borderRadius: 8, background: '#fff' }} />
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--hp-muted)' }}><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Building document…</div>
          )}
          {previewUrl && (
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', color: 'var(--hp-gold)', marginTop: 6 }}>
              <Eye size={12} /> Open full document
            </a>
          )}

          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>Your signature</div>
            <SignaturePad onChange={setSignature} defaultName={contract.driverName || contract.driverLogin} />
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, fontSize: '0.82rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
            <span>I, {driverName}, have read and agree to be bound by this Vehicle Lease Agreement.</span>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid var(--hp-border)' }}>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={!signature || !agree || busy} onClick={submit}>
            {busy ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Submitting…</> : <><Lock size={14} /> Sign &amp; submit</>}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function LeaseSignCard({ shift, onCountChange }) {
  const driverLogin = (shift?.driver || '').toUpperCase();
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(null);

  // Keep the parent (My Van tab dot) in sync with how many leases are pending.
  const setPendingSynced = useCallback((next) => {
    setPending(prev => {
      const val = typeof next === 'function' ? next(prev) : next;
      if (onCountChange) onCountChange(val.length);
      return val;
    });
  }, [onCountChange]);

  const load = useCallback(async () => {
    try {
      const d = await fetch(CONTRACT_URL, { method: 'POST', body: JSON.stringify({ action: 'getMyContracts' }) }).then(r => r.json());
      if (d.success) setPendingSynced(d.contracts || []);
    } catch { /* silent — portal shouldn't break on this */ }
  }, [setPendingSynced]);

  useEffect(() => { if (driverLogin) load(); }, [driverLogin, load]);

  const decline = async (c) => {
    const reason = window.prompt(`Decline the lease for ${c.vehicle?.rego || c.rego}? Optionally add a reason:`, '');
    if (reason === null) return; // cancelled
    try {
      const res = await fetch(CONTRACT_URL, { method: 'POST', body: JSON.stringify({ action: 'declineContract', id: c.id, reason }) }).then(r => r.json());
      if (res.success) setPendingSynced(prev => prev.filter(x => x.id !== c.id));
      else alert(`Could not decline: ${res.error || 'unknown error'}`);
    } catch (e) { alert(`Error: ${e.message}`); }
  };

  if (!pending.length) return null;

  return (
    <div style={{ width: '100%', marginBottom: 16 }}>
      {pending.map(c => (
        // A narrow phone kept the buttons at full width and crushed the text to one
        // word per line — worse, they ended up sitting on top of it. Wrap instead.
        <div key={c.id} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '14px 16px', borderRadius: 12, background: 'rgba(212,175,55,0.08)', border: '1px solid var(--hp-gold)', marginBottom: 10 }}>
          <FileSignature size={22} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
          <div style={{ flex: '1 1 190px', minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Vehicle lease agreement to sign</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
              {c.vehicle?.rego || c.rego}{c.leaseRate > 0
                ? ` · ${(c.leaseRate * 100).toFixed(1)}% of gross${c.leaseCap > 0 ? ` (capped at $${Number(c.leaseCap).toLocaleString('en-AU')})` : ''} + GST`
                : c.weeklyFee ? ` · $${Number(c.weeklyFee).toFixed(2)} + GST per 4 weeks` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 'auto' }}>
            <button className="btn btn-primary btn-sm" onClick={() => setOpen(c)}>Review &amp; sign</button>
            <button className="btn btn-ghost btn-sm" onClick={() => decline(c)}>Decline</button>
          </div>
        </div>
      ))}

      {open && (
        <LeaseSignModal
          contract={open}
          onClose={() => setOpen(null)}
          onSigned={(id) => setPendingSynced(prev => prev.filter(c => c.id !== id))}
        />
      )}
    </div>
  );
}

// Moved to ./SignaturePad (so a tenant build can use it without the lease code); kept
// exported here for the screens that import it from this file.
export { SignaturePad };
